import { supabase } from '@/lib/supabase';
import type { TermsPolicy, TermsPolicyCategory, ApiResponse } from '@/types';

class TermsPolicyService {
  /**
   * 모든 활성화된 약관 및 정책 조회
   */
  async getAllTermsPolicies(): Promise<ApiResponse<TermsPolicy[]>> {
    try {
      const { data, error } = await supabase
        .from('terms_policies')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching terms policies:', error);
        return {
          success: false,
          error: '약관 및 정책을 불러오는데 실패했습니다.',
        };
      }

      return {
        success: true,
        data: data || [],
      };
    } catch (error) {
      console.error('Unexpected error fetching terms policies:', error);
      return {
        success: false,
        error: '예상치 못한 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 카테고리별 약관 및 정책 조회
   */
  async getTermsPoliciesByCategory(category: TermsPolicyCategory): Promise<ApiResponse<TermsPolicy[]>> {
    try {
      const { data, error } = await supabase
        .from('terms_policies')
        .select('*')
        .eq('category', category)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching terms policies by category:', error);
        return {
          success: false,
          error: '약관 및 정책을 불러오는데 실패했습니다.',
        };
      }

      return {
        success: true,
        data: data || [],
      };
    } catch (error) {
      console.error('Unexpected error fetching terms policies by category:', error);
      return {
        success: false,
        error: '예상치 못한 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 특정 약관/정책 조회
   */
  async getTermsPolicyById(id: string): Promise<ApiResponse<TermsPolicy>> {
    try {
      const { data, error } = await supabase
        .from('terms_policies')
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching terms policy:', error);
        return {
          success: false,
          error: '약관 및 정책을 찾을 수 없습니다.',
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Unexpected error fetching terms policy:', error);
      return {
        success: false,
        error: '예상치 못한 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 제목으로 특정 약관/정책 조회
   */
  async getTermsPolicyByTitle(title: string): Promise<ApiResponse<TermsPolicy>> {
    try {
      const { data, error } = await supabase
        .from('terms_policies')
        .select('*')
        .eq('title', title)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching terms policy by title:', error);
        return {
          success: false,
          error: '약관 및 정책을 찾을 수 없습니다.',
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Unexpected error fetching terms policy by title:', error);
      return {
        success: false,
        error: '예상치 못한 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 약관/정책 생성 (관리자용)
   */
  async createTermsPolicy(termsPolicy: Omit<TermsPolicy, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<TermsPolicy>> {
    try {
      const { data, error } = await supabase
        .from('terms_policies')
        .insert(termsPolicy)
        .select()
        .single();

      if (error) {
        console.error('Error creating terms policy:', error);
        return {
          success: false,
          error: '약관 및 정책 생성에 실패했습니다.',
        };
      }

      return {
        success: true,
        data: data,
        message: '약관 및 정책이 성공적으로 생성되었습니다.',
      };
    } catch (error) {
      console.error('Unexpected error creating terms policy:', error);
      return {
        success: false,
        error: '예상치 못한 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 약관/정책 수정 (관리자용)
   */
  async updateTermsPolicy(id: string, updates: Partial<Omit<TermsPolicy, 'id' | 'created_at' | 'updated_at'>>): Promise<ApiResponse<TermsPolicy>> {
    try {
      const { data, error } = await supabase
        .from('terms_policies')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating terms policy:', error);
        return {
          success: false,
          error: '약관 및 정책 수정에 실패했습니다.',
        };
      }

      return {
        success: true,
        data: data,
        message: '약관 및 정책이 성공적으로 수정되었습니다.',
      };
    } catch (error) {
      console.error('Unexpected error updating terms policy:', error);
      return {
        success: false,
        error: '예상치 못한 오류가 발생했습니다.',
      };
    }
  }

  /**
   * 약관/정책 삭제 (관리자용)
   */
  async deleteTermsPolicy(id: string): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('terms_policies')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting terms policy:', error);
        return {
          success: false,
          error: '약관 및 정책 삭제에 실패했습니다.',
        };
      }

      return {
        success: true,
        message: '약관 및 정책이 성공적으로 삭제되었습니다.',
      };
    } catch (error) {
      console.error('Unexpected error deleting terms policy:', error);
      return {
        success: false,
        error: '예상치 못한 오류가 발생했습니다.',
      };
    }
  }
}

export const termsPolicyService = new TermsPolicyService();